<template>
  <b-container class="mt-4">
    <b-row class="justify-content-md-center">
      <b-col cols="12" lg="6" v-if="isLoading || isPollingTimedOut">
        <div class="bg-light p-5 mt-5 rounded-lg d-flex flex-column align-items-center" v-test="'loading'">
          <template v-if="isLoading">
            <b-spinner />
            <h4 class="mt-3">Validating your file...</h4>
            <p class="mb-0">This may take a while</p>
          </template>
          <template v-if="isPollingTimedOut">
            <h4 class="mt-3">This is taking longer than usual</h4>
            <p class="mb-3">It appears we haven't yet validated your file. This can be normal for larger uploads. You can try reloading this page to keep checking, or try uploading a smaller file.</p>
            <b-button @click="doReset" v-test="'timeout reset button'">
              <b-icon-arrow-clockwise class="mr-2" /> Choose a different file
            </b-button>
          </template>
        </div>
      </b-col>
      <b-col cols="12" lg="10" v-else-if="resultsMeta">
        <div class="rounded-lg bg-white d-flex p-3 align-items-center w-100">
          <h1 class="mb-0 mr-3">
            <b-icon-percent />
          </h1>
          <div class="flex-1 mr-3">
            <h5>Data Quality</h5>
            <p class="mb-0">Percentage of the rows you provided that have enough information to be matched against a call.</p>
          </div>
          <div class="ml-auto d-flex flex-column align-items-center">
            <h3 class="mb-0" v-test="'match rate'">{{ resultsMeta.getPotentialMatchPercent().toFixed(2) }}%</h3>
            <small class="test-center">of {{ resultsMeta.getTotalCount() }} rows</small>
          </div>
        </div>
        <div class="rounded-lg bg-white d-flex p-3 align-items-center w-100 mt-3" v-if="resultsMeta.getWarningCount()">
          <h1 class="mb-0 mr-3">
            <b-icon-exclamation-diamond-fill variant="warning" />
          </h1>
          <div class="flex-1 mr-3">
            <h5>Warnings</h5>
            <p class="mb-0">The number of rows that have a reduced chance of being matched due to missing information.</p>
            <p class="mb-0 text-whale">These rows will still be processed.</p>
          </div>
          <div class="ml-auto d-flex flex-column align-items-center">
            <h3 class="mb-0" v-test="'warning count'">{{ resultsMeta.getWarningCount() }}</h3>
            <small class="test-center">of {{ resultsMeta.getTotalCount() }} rows</small>
          </div>
        </div>
        <div class="rounded-lg bg-white d-flex p-3 align-items-center flex-wrap w-100 mt-3" v-if="error.getErrorCode() === 400 || error.getErrorCode() === 500 || error.getErrorDescription() !== ''">
          <h1 class="mb-0 mr-3">
            <b-icon-exclamation-triangle-fill variant="danger" />
          </h1>
          <div class="flex-1 mr-3">
            <h5>Errors</h5>
            <p class="mb-0">Your file contains required information or data that's in a format we didn't expect.</p>
            <p class="mb-0 text-whale">Rows with errors will be ignored</p>
          </div>
          <div class="ml-auto d-flex flex-column align-items-center">
            <h3 class="mb-0" v-test="'error count'">{{ resultsMeta.getErrorCount() }}</h3>
            <small class="test-center">of {{ resultsMeta.getTotalCount() }} rows</small>
          </div>
          <hr class="line-above" />
          <!-- Your file is missing mandatory columns -->
          <div class="rounded-box w-100 mt-3 bg-light border p-2">
            <p class="mb-0 text-whale" data-test-get-error-description="error">{{ error.getErrorDescription() }}</p>
          </div>
          <!-- The following rows have errors -->
          <div class="w-100" v-if="resultsMeta.getErrorRowsList() !== ''">
            <div class="rounded-box w-100 mt-3 bg-light border p-2">
              <p class="mb-0 text-whale" data-test="error-header-rows">The following rows have errors</p>
            </div>
            <div  class="w-100 bg-white border-bottom border-left border-right p-3">
              <p class="mb-0" data-test="error-rows-list">{{ resultsMeta.getErrorRowsList() }}</p>
            </div>
          </div>
        </div>
        <div class="rounded-lg bg-white d-flex p-3 align-items-center flex-wrap w-100 mt-3" v-else>
          <h1 class="mb-0 mr-3">
            <b-icon-check-circle-fill variant="success" />
          </h1>
          <div class="flex-1 mr-3">
            <h5 data-test="successful-upload">Your file was uploaded successfully</h5>
          </div>
        </div>
        <div class="bg-light mt-4 p-3 rounded-lg" v-if="resultsMeta.getWarningCount() || resultsMeta.getErrorCount()" v-test="'validation file'">
          <h5>Validation File</h5>
          <p>We've prepared a copy of your CSV with any warnings or errors that were found. <template v-if="resultsMeta.getPotentialMatchPercent() !== 0">You may check this file before choosing how to proceed.</template></p>
          <p class="text-center mb-0">
            <b-button variant="info" type="download" :href="$store.state.validationResults.getUrl()" v-test="'file download'"><b-icon-cloud-download class="mr-2" /> View Validation File</b-button>
          </p>
        </div>
        <div class="bg-white mt-4 p-3 rounded-lg" v-if="resultsMeta.getPotentialMatchPercent() === 0" v-test="'no potential matches'">
          <h5><b-icon-exclamation-triangle-fill variant="danger" class="mr-1" /> Unable To Continue</h5>
          <p class="mb-0">We cannot process a file with a potential match rate of 0%. Please correct the errors present in your file and upload again.</p>
        </div>
        <div class="d-flex justify-content-between w-100 mt-4">
          <b-button @click="doReset" v-test="'reset button'">
            <b-icon-arrow-clockwise class="mr-2" /> Choose a different file
          </b-button>
          <b-button
            @click="doQueue"
            variant="success"
            :disabled="resultsMeta.getPotentialMatchPercent() === 0 || isQueuing"
            v-test="'continue button'"
          >
            <b-icon-cloud-upload v-if="!isQueuing" class="mr-2" />
            <b-spinner small v-else class="mr-2" />
            Continue with this file
          </b-button>
        </div>
      </b-col>
      <b-col cols="12" lg="10" v-else-if="$store.state.networkError">
        <div class="rounded-lg bg-white d-flex p-3 align-items-center flex-wrap w-100 mt-3"
          v-if="$store.state.networkError">
          <h1 class="mb-0 mr-3">
            <b-icon-exclamation-triangle-fill variant="danger" />
          </h1>
          <div class="flex-1 mr-3">
            <h5>Errors</h5>
            <p class="mb-0">Network Error</p>
          </div>
          <hr class="line-above" />
          <div class="w-100" data-test-network-connectivity-issue="error">
            <div class="rounded-box w-100 mt-3 bg-light border p-2">
              <p class="mb-0 text-whale" data-test="error-header-network">Your file was partially uploaded due to a network connectivity issue.</p>
            </div>
            <div class="w-100 bg-white border-bottom border-left border-right p-3">
              <p class="mb-0">Please re-upload your file to include any missing data</p>
            </div>
          </div>
          <div class="d-flex justify-content-between w-100 mt-4" data-test-network-error-div="data-test-network-error-div">
            <b-button @click="doReset" v-test="'reset button network error'" data-test-network-error-button="network-error-reset-button">
              <b-icon-arrow-clockwise class="mr-2" /> Choose a different file
            </b-button>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script lang="ts">
import { Component, Emit, Vue } from 'vue-property-decorator'
import {
  BIconPercent,
  BIconExclamationDiamondFill,
  BIconExclamationTriangleFill,
  BIconCloudDownload,
  BIconArrowClockwise,
  BIconCloudUpload,
  BIconInfoCircle,
  BIconCheckCircleFill
} from 'bootstrap-vue'
import { get } from 'lodash'

// 5 seconds
const pollInterval = 5000

// 5 minutes
const maxPollingTime = 300000

@Component({
  components: {
    BIconPercent,
    BIconExclamationDiamondFill,
    BIconExclamationTriangleFill,
    BIconCloudDownload,
    BIconArrowClockwise,
    BIconCloudUpload,
    BIconInfoCircle,
    BIconCheckCircleFill
  }
})
export default class ResultsView extends Vue {
  poller: number | null = null
  isLoading = true
  isQueuing = false
  pollingTime = 0

  get isPollingTimedOut () {
    return this.pollingTime >= maxPollingTime
  }

  async mounted () {
    const filename = get(this.$route, 'params.filename')

    if (!filename) {
      await this.$store.dispatch('reset')
      await this.$router.push('/')
    }

    this.$store.commit('setFilename', filename)

    if (this.$store.state.networkError) {
      this.isLoading = false
    }

    try {
      await this.$store.dispatch('getValidationResults')
    } catch (e) {
      const status = get(e, 'response.status', 0)

      if (status === 404) {
        this.poller = window.setInterval(
          async () => {
            if (this.pollingTime < maxPollingTime) {
              try {
                await this.$store.dispatch('getValidationResults')
              } catch (err) {
                this.pollingTime += pollInterval

                if (get(err, 'response.status', 0) === 404) {
                  return
                }
              }
            }

            this.isLoading = false
            if (this.poller) {
              window.clearInterval(this.poller)
            }
          }, pollInterval
        )

        return
      }
    }

    this.isLoading = false
  }

  get error () {
    if (this.$store.state.validationResults === null) {
      return null
    }

    return this.$store.state.validationResults.getError()
  }

  get resultsMeta () {
    if (this.$store.state.validationResults === null) {
      return null
    }

    return this.$store.state.validationResults.getMetadata()
  }

  beforeDestroy () {
    this.appDeactivated()
  }

  async doQueue () {
    this.isQueuing = true

    try {
      await this.$store.dispatch('storeMapping')
      await this.$store.dispatch('submitBatch')
      await this.$store.dispatch('reset')
      if (!this.$store.state.uploadRoutesOnly) {
        await this.$router.push('/upload-history')
      }

      this.$bvToast.toast('Your uploaded CSV is now processing. Any matches will appear in the Transaction Log.', {
        title: 'CSV Uploaded',
        variant: 'success',
        autoHideDelay: 5000
      })
    } catch (e) {
      this.$bvToast.toast('We encountered a problem queuing this file for processing. Please try again.', {
        title: 'Error',
        variant: 'danger',
        autoHideDelay: 5000
      })
    }

    await this.$mixpanelService.track('settings.smartMatch.queue', {
      filename: get(this.$route, 'params.filename'),
      totalRows: this.resultsMeta?.getTotalCount(),
      potentialMatchPercentage: this.resultsMeta?.getPotentialMatchPercent()
    })

    if (this.$store.state.uploadRoutesOnly) {
      // give time for toast to be read before going to an external route
      setTimeout(async () => {
        this.goToExternalHistoryRoute()

        // reset router path
        await this.$router.push('/')
      }, 4000)
    }

    this.isQueuing = false
  }

  @Emit('go-to-external-route')
  goToExternalHistoryRoute () {
    return 'history'
  }

  appDeactivated () {
    if (this.poller) {
      window.clearInterval(this.poller)
    }
  }

  async doReset () {
    await this.$store.dispatch('reset')
    await this.$router.push('/')
  }
}
</script>

<style lang="scss">
.flex-1 {
  flex: 1 !important;
}
</style>
